import {
    SpriteMaterial,
    Sprite,
    Object3D,
    CanvasTexture,
    Clock,
    TextureLoader,
} from 'three';
import { TweenMax } from 'gsap';

class Light extends Object3D {
    constructor(opts) {
        super();

        this.options = {
            camera: null,
            count: 100,
            materialsCount: 1,
            materialSize: 8,
            materialForm: 'square',
            color: '#FFFFFF',
            lifetime: 2,
            sparkLifetime: 2,
            range: 90,
            sparkRange: 70,
            minScale: 5,
            maxScale: 5,
            maxDepth: 0,
            alpha: 0.8,
            starTextures: [
                '/img/stars/stars_0000_1.png',
                '/img/stars/stars_0001_2.png',
                '/img/stars/stars_0002_3.png',
                '/img/stars/stars_0003_4.png',
                '/img/stars/stars_0004_5.png',
                '/img/stars/stars_0005_6.png',
                '/img/stars/stars_0006_7.png',
                '/img/stars/stars_0007_8.png',
                '/img/stars/stars_0008_9.png',
                '/img/stars/stars_0009_10.png',
                '/img/stars/stars_0010_11.png',
                '/img/stars/stars_0011_12.png',
                '/img/stars/stars_0012_13.png',
                '/img/stars/stars_0013_14.png',
                '/img/stars/stars_0014_15.png',
            ],
            sparkTexture: '/img/spark.png',
            minSparkAlpha: 0.2,
            maxSparkAlpha: 1.0,
            minSparkScale: 2,
            maxSparkScale: 5,
            ...opts,
        };

        this.camera = this.options.camera;
        this.materialAlpha = 0;
        this.started = false;
        this.starsVisible = true;
        this.globalLifetime = 1;
        this.stars = [];
        this.sparks = [];
        this.clock = new Clock(false);
        const textureLoader = new TextureLoader();
        this.sparkTexture = textureLoader.load(this.options.sparkTexture);
        this.starTextures = this.options.starTextures.map(texture => (
            textureLoader.load(texture)
        ));
    }

    buildCanvas() {
        const { materialSize, materialForm, color } = this.options;
        const canvas = document.createElement('canvas');
        canvas.width = materialSize;
        canvas.height = materialSize;
        const context = canvas.getContext('2d');
        context.fillStyle = color;

        if (materialForm === 'circle') {
            context.beginPath();
            context.arc(materialSize / 2, materialSize / 2, materialSize / 2, 0, 2 * Math.PI);
            context.fill();
        } else {
            const dotCount = Math.max(Math.random() * 4, 2);
            const halfSize = materialSize / 2;
            for (let i = 0; i < dotCount; i += 1) {
                const x = Math.floor(Math.random() * halfSize);
                const y = Math.floor(Math.random() * halfSize);
                // const x = materialSize / 2;
                // const y = materialSize / 2;
                context.fillStyle = color;
                context.fillRect(
                    x,
                    y,
                    halfSize + Math.ceil(Math.random() * halfSize),
                    halfSize + Math.ceil(Math.random() * halfSize),
                );
            }
        }

        return canvas;
    }

    buildMaterial() {
        // const { sparkTexture } = this.options;
        // const canvas = this.buildCanvas();
        // const texture = new CanvasTexture(canvas);
        const material = new SpriteMaterial({
            // map: texture,
            map: this.starTextures[Math.floor(Math.random() * this.starTextures.length)],
            transparent: true,
            depthWrite: false,
        });
        return material;
    }

    buildMaterials() {
        const { materialsCount } = this.options;
        const materials = [];
        for (let j = 0; j < materialsCount; j += 1) {
            materials.push(this.buildMaterial());
        }
        return materials;
    }

    buildSparkMaterial() {
        // const { sparkTexture } = this.options;
        return new SpriteMaterial({
            map: this.sparkTexture,
            transparent: true,
            depthWrite: false,
        });
    }

    build() {
        const { count } = this.options;
        const lifeStep = 1 / count;
        let currentLife = 0;
        for (let i = 0; i < count; i += 1) {
            const star = this.buildStar(currentLife);
            this.stars.push(star);
            this.add(star);
            currentLife += lifeStep;
        }
    }

    buildStar(currentLife) {
        const {
            lifetime,
            range,
            minScale,
            maxScale,
            maxDepth,
            alpha,
        } = this.options;
        // const materialIndex = Math.floor(Math.random() * materialsCount);
        // const material = this.materials[materialIndex].clone();
        const material = this.buildMaterial();
        const star = new Sprite(material);
        const life = currentLife;
        const scale = minScale + (Math.random() * (maxScale - minScale));
        star.lifeLeft = life * lifetime;
        star.material.opacity = alpha * this.materialAlpha * Math.sin(life * Math.PI);
        const randomRadius = Math.random() * range;
        const randomAngle = (Math.random() * 360) * (Math.PI / 180);
        const x = randomRadius * Math.cos(randomAngle);
        const y = randomRadius * Math.sin(randomAngle);
        star.position.set(x, y, -(maxDepth * Math.random()));
        star.rotation.set(0, 0, 0);
        star.scale.set(scale, scale, 1.0);
        return star;
    }

    buildSpark() {
        const {
            sparkRange,
            sparkLifetime,
            minSparkAlpha,
            maxSparkAlpha,
            minSparkScale,
            maxSparkScale,
        } = this.options;
        const material = this.buildSparkMaterial();
        const spark = new Sprite(material);
        const randomRadius = Math.random() * sparkRange;
        const randomAngle = (Math.random() * 360) * (Math.PI / 180);
        const x = randomRadius * Math.cos(randomAngle);
        const y = randomRadius * Math.sin(randomAngle);
        const life = Math.random();
        spark.lifeLeft = life * sparkLifetime;
        spark.material.opacity = (minSparkAlpha + (
            (maxSparkAlpha - minSparkAlpha) * Math.sin(life * Math.PI)
        )) * this.materialAlpha;
        spark.position.set(x, y, 0);
        const scale = minSparkScale + (Math.random() * (maxSparkScale - minSparkScale));
        spark.scale.set(scale, scale, scale);
        return spark;
    }

    addSparks(delta) {
        for (let i = 0; i < delta; i += 1) {
            const spark = this.buildSpark();
            this.sparks.push(spark);
            this.add(spark);
        }
    }

    start() {
        this.started = true;
        this.clock.start();
    }

    stop() {
        this.started = false;
        this.clock.stop();
    }


    tick() {
        if (!this.started || !this.visible) {
            return;
        }

        const delta = this.clock.getDelta();
        this.tickStars(delta);
        this.tickSparks(delta);
    }

    /* eslint-disable no-param-reassign */
    tickStars(delta) {
        const {
            lifetime,
            alpha,
            count,
        } = this.options;
        const lastIndex = count - 1;
        if (!this.starsVisible) {
            return;
        }
        this.stars.forEach((star, index) => {
            star.lifeLeft -= delta % lifetime;
            if (star.lifeLeft <= 0) {
                star.lifeLeft = lifetime;
                star.material.opacity = 0;
            } else {
                const life = star.lifeLeft / lifetime;
                star.material.opacity = alpha * this.materialAlpha * Math.sin(life * Math.PI);
            }
            const position = index / lastIndex;
            if (this.globalLifetime < 1) {
                const rolloutLifetime = this.globalLifetime * Math.sin(position * (Math.PI / 2));
                star.material.opacity *= rolloutLifetime;
            }
            if (star.material.opacity === 0 && star.visible) {
                star.visible = false;
            } else if (star.material.opacity > 0 && !star.visible) {
                star.visible = true;
            }
        });
    }


    tickSparks(delta) {
        const { sparkLifetime, minSparkAlpha, maxSparkAlpha } = this.options;
        this.sparks.forEach((spark) => {
            spark.lifeLeft -= (delta % sparkLifetime) * Math.random();
            if (spark.lifeLeft <= 0) {
                spark.lifeLeft = sparkLifetime;
                spark.material.opacity = minSparkAlpha * this.materialAlpha;
            } else {
                const life = spark.lifeLeft / sparkLifetime;
                spark.material.opacity = (minSparkAlpha + (
                    (maxSparkAlpha - minSparkAlpha) * Math.sin(life * Math.PI)
                )) * this.materialAlpha;
            }
        });
    }
    /* eslint-enable no-param-reassign */

    setSize(width, height) {

    }

    getFadeOutTimeline() {
        return TweenMax.to(this, 2, {
            globalLifetime: 0,
            onComplete: () => {
                this.starsVisible = false;
            }
        });
    }

    set alpha(alpha) {
        this.materialAlpha = alpha;
        if (alpha <= 0) {
            this.visible = false;
            this.tick();
        } else if (!this.visible) {
            this.visible = true;
        }
    }

    get alpha() {
        return this.materialAlpha;
    }
}

export default Light;



// WEBPACK FOOTER //
// ./lib/Stars.js